<template>
  <div>
    <AppNavbar />
    <div class="container mt-5">
      <AppHeader title="Ventas Facturas">
        <template #buttons>
          <button
            type="button"
            class="btn btn-outline-secondary"
            :disabled="cargando"
            @click="cargarVentas()"
          >
            <i class="bi bi-arrow-repeat me-1"></i>
            Recargar
          </button>
        </template>
      </AppHeader>

      <div class="table-responsive mt-4 card p-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h2 class="mb-0">Listado de ventas</h2>
          <small class="text-muted">
            Página {{ paginaActual }} de {{ totalPaginas }} · {{ totalRegistros }} registros
          </small>
        </div>
        <table class="table table-striped align-middle">
          <thead>
            <tr>
              <th>N° factura</th>
              <th>Cliente</th>
              <th>Documento</th>
              <th>Fecha</th>
              <th class="text-end">Total IVA inc.</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="cargando">
              <td colspan="7" class="text-center py-4">Cargando ventas...</td>
            </tr>
            <tr v-else-if="!ventas.length">
              <td colspan="7" class="text-center py-4">No hay ventas para mostrar.</td>
            </tr>
            <tr v-else v-for="venta in ventas" :key="venta.id_venta">
              <td>{{ venta.nro_factura || '—' }}</td>
              <td>{{ venta.nombre_razon_social || '—' }}</td>
              <td>{{ venta.nro_documento || '—' }}</td>
              <td>{{ formatearFecha(venta.fecha_emision) }}</td>
              <td class="text-end">{{ formatearMonto(venta.total_iva_incluido || venta.total_sin_iva) }}</td>
              <td>
                <span :class="['badge', badgeClass(venta.estado)]">
                  {{ (venta.estado || '—').toUpperCase() }}
                </span>
              </td>
              <td>
                <div class="d-inline-flex flex-nowrap gap-1">
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    @click="abrirDetalle(venta.id_venta)"
                  >
                    <i class="bi bi-eye me-1"></i>
                    Ver detalle
                  </button>
                  <!-- FE-RTK-004: el botón se OCULTA (no se deshabilita) si la
                       impresión local está apagada o la venta está anulada. Un
                       botón deshabilitado invita a preguntar por qué; uno
                       ausente no promete nada. -->
                  <button
                    v-if="puedeImprimir(venta)"
                    type="button"
                    class="btn btn-outline-secondary btn-sm"
                    :disabled="imprimiendoVentaId === venta.id_venta"
                    @click="reimprimirTicket(venta)"
                  >
                    <span
                      v-if="imprimiendoVentaId === venta.id_venta"
                      class="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <i v-else class="bi bi-printer me-1"></i>
                    Imprimir
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <AppPagination
        class="mt-3"
        :currentPage="paginaActual"
        :totalPages="totalPaginas"
        @page-changed="cambiarPagina"
      />
    </div>

    <div
      v-if="detalleModalAbierto"
      class="modal fade show d-block"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
    >
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Detalle de la venta
              <template v-if="detalleVenta?.cabecera?.nro_factura">
                #{{ detalleVenta.cabecera.nro_factura }}
              </template>
            </h5>
            <button type="button" class="btn-close" aria-label="Close" @click="cerrarDetalle"></button>
          </div>
          <div class="modal-body">
            <div v-if="detalleCargando" class="text-center py-4">Cargando detalle...</div>
            <div v-else-if="detalleError" class="alert alert-danger mb-0">
              {{ detalleError }}
            </div>
            <template v-else-if="detalleVenta">
              <section class="mb-3">
                <h6 class="fw-bold">Cabecera</h6>
                <div class="row g-2">
                  <div class="col-md-3"><strong>Comprobante:</strong> {{ detalleVenta.cabecera.nro_comprobante }}</div>
                  <div class="col-md-3"><strong>Timbrado:</strong> {{ detalleVenta.cabecera.timbrado }}</div>
                  <div class="col-md-3"><strong>Fecha emisión:</strong> {{ formatearFecha(detalleVenta.cabecera.fecha_emision) }}</div>
                  <div class="col-md-3"><strong>Moneda:</strong> {{ detalleVenta.cabecera.tipo_moneda }}</div>
                  <div class="col-md-3"><strong>Condición:</strong> {{ detalleVenta.cabecera.credito_contado }}</div>
                  <div class="col-md-3"><strong>Documento:</strong> {{ detalleVenta.cabecera.tipo_documento }} {{ detalleVenta.cabecera.nro_documento }}</div>
                  <div class="col-md-3"><strong>Cliente:</strong> {{ detalleVenta.cabecera.nombre_razon_social }}</div>
                  <div class="col-md-3"><strong>Teléfono:</strong> {{ detalleVenta.cabecera.telefono_celular || '—' }}</div>
                  <div class="col-md-6"><strong>Dirección:</strong> {{ detalleVenta.cabecera.direccion || '—' }}</div>
                  <div class="col-md-3"><strong>Estado:</strong> {{ detalleVenta.cabecera.estado }}</div>
                  <div class="col-md-3"><strong>Pendiente:</strong> {{ detalleVenta.cabecera.pendiente ? 'Sí' : 'No' }}</div>
                </div>
              </section>

              <section class="mb-3">
                <div class="d-flex justify-content-between align-items-center">
                  <h6 class="fw-bold mb-2">Productos</h6>
                  <small class="text-muted">IVA incl. y cantidades según detalle</small>
                </div>
                <div class="table-responsive">
                  <table class="table table-sm table-bordered align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Código</th>
                        <th>Descripción</th>
                        <th class="text-end">Cant.</th>
                        <th>UM</th>
                        <th class="text-end">Precio unit.</th>
                        <th class="text-end">Descuento</th>
                        <th class="text-end">IVA</th>
                        <th class="text-end">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="producto in detalleVenta.detalles_productos" :key="producto.id_detalle">
                        <td>{{ producto.codigo_plu_balanza || producto.codigo_producto || producto.codigo_barras || '—' }}</td>
                        <td>
                          {{ producto.nombre_producto || producto.descripcion || '—' }}
                          <div v-if="producto.origen_lectura && producto.origen_lectura !== 'NORMAL'" class="small text-muted">
                            {{ producto.origen_lectura }}<span v-if="producto.codigo_balanza_raw"> · {{ producto.codigo_balanza_raw }}</span>
                          </div>
                        </td>
                        <td class="text-end">{{ formatCantidadDetalle(producto) }}</td>
                        <td>{{ formatUnidadDetalle(producto) }}</td>
                        <td class="text-end">{{ formatPrecioDetalle(producto) }}</td>
                        <td class="text-end">{{ formatearMonto(producto.descuento) }}</td>
                        <td class="text-end">{{ formatearMonto(producto.iva) }}</td>
                        <td class="text-end">
                          {{ formatearMonto(calcularSubtotalDetalle(producto)) }}
                        </td>
                      </tr>
                      <tr v-if="!detalleVenta.detalles_productos?.length">
                        <td colspan="8" class="text-center">Sin productos registrados.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section class="mb-2">
                <h6 class="fw-bold mb-2">Totales por IVA</h6>
                <div class="table-responsive">
                  <table class="table table-sm table-bordered align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Tipo IVA</th>
                        <th class="text-end">Gravado</th>
                        <th class="text-end">IVA</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="iva in detalleVenta.detalles_ivas" :key="iva.id_venta_iva">
                        <td>{{ iva.tipo_iva_id }}</td>
                        <td class="text-end">{{ formatearMonto(iva.gravado) }}</td>
                        <td class="text-end">{{ formatearMonto(iva.iva) }}</td>
                      </tr>
                      <tr v-if="!detalleVenta.detalles_ivas?.length">
                        <td colspan="3" class="text-center">Sin totales de IVA.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section class="d-flex justify-content-end gap-3">
                <div class="text-end">
                  <div class="small text-muted">Total sin IVA</div>
                  <div class="fw-bold">{{ formatearMonto(detalleVenta.cabecera.total_sin_iva) }}</div>
                </div>
                <div class="text-end">
                  <div class="small text-muted">Total IVA</div>
                  <div class="fw-bold">{{ formatearMonto(detalleVenta.cabecera.total_iva) }}</div>
                </div>
                <div class="text-end">
                  <div class="small text-muted">Total IVA incluido</div>
                  <div class="fw-bold fs-5">{{ formatearMonto(detalleVenta.cabecera.total_iva_incluido) }}</div>
                </div>
              </section>
            </template>
          </div>
          <div class="modal-footer">
            <!-- FE-RTK-005: el modal NO se cierra al imprimir. Es donde el
                 operador confirma que es la venta correcta antes de gastar
                 papel, y donde puede reintentar si hace falta. -->
            <button
              v-if="puedeImprimirDetalle"
              type="button"
              class="btn btn-outline-secondary"
              :disabled="imprimiendoVentaId === detalleVentaId"
              @click="reimprimirDesdeDetalle"
            >
              <span
                v-if="imprimiendoVentaId === detalleVentaId"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              <i v-else class="bi bi-printer me-1"></i>
              Imprimir ticket
            </button>
            <button type="button" class="btn btn-secondary" @click="cerrarDetalle">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="detalleModalAbierto" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
import AppNavbar from '@/components/AppNavbar.vue';
import AppHeader from '@/components/AppHeader.vue';
import AppPagination from '@/components/AppPagination.vue';
import apiService from '@/services/apiService.js';
import invoiceReprintService from '@/services/invoiceReprintService';

export default {
  name: 'VentasFacturasView',
  components: {
    AppNavbar,
    AppHeader,
    AppPagination
  },
  data() {
    return {
      ventas: [],
      paginaActual: 1,
      pageSize: 5,
      totalPaginas: 1,
      totalRegistros: 0,
      cargando: false,
      // El estado en vuelo es POR FILA: un flag global deshabilitaría toda la
      // tabla mientras imprime una sola venta (D6 del PLAN).
      imprimiendoVentaId: null,
      detalleVentaIdAbierto: 0,
      sortBy: 'nro_comprobante',
      sortDir: 'asc',
      detalleModalAbierto: false,
      detalleCargando: false,
      detalleVenta: null,
      detalleError: ''
    };
  },
  mounted() {
    this.cargarVentas();
  },
  computed: {
    detalleVentaId() {
      return this.detalleVentaIdAbierto
        || Number(this.detalleVenta?.cabecera?.id_venta || 0);
    },
    puedeImprimirDetalle() {
      return this.puedeImprimir({
        id_venta: this.detalleVentaId,
        estado: this.detalleVenta?.cabecera?.estado
      });
    }
  },
  methods: {
    async cargarVentas(page = this.paginaActual) {
      this.cargando = true;
      try {
        const params = {
          pagina: page,
          pageSize: this.pageSize,
          sortBy: this.sortBy,
          sortDir: this.sortDir
        };

        const { data } = await apiService.get('/api/sales', params);
        const payload = data?.data || {};

        this.ventas = Array.isArray(payload.items) ? payload.items : [];
        this.paginaActual = Number(payload.pagina || page) || 1;
        this.totalPaginas = Number(payload.totalPages || 1) || 1;
        this.totalRegistros = Number(payload.total || this.ventas.length) || this.ventas.length;
      } catch (error) {
        console.error('Error al cargar ventas:', error);
        this.ventas = [];
        this.totalPaginas = 1;
        this.totalRegistros = 0;
      } finally {
        this.cargando = false;
      }
    },
    cambiarPagina(page) {
      if (page === this.paginaActual) return;
      this.paginaActual = page;
      this.cargarVentas(page);
    },
    abrirDetalle(idVenta) {
      this.detalleModalAbierto = true;
      this.detalleVenta = null;
      this.detalleError = '';
      // Se guarda el id con el que se abrió el modal en vez de leerlo de la
      // respuesta: `/api/sales/detail` no garantiza `cabecera.id_venta`, y
      // depender de la forma del payload para saber qué imprimir es frágil.
      this.detalleVentaIdAbierto = Number(idVenta) || 0;
      this.obtenerDetalle(idVenta);
    },
    async obtenerDetalle(idVenta) {
      this.detalleCargando = true;
      try {
        const { data } = await apiService.get('/api/sales/detail', { id_venta: idVenta });
        const detalle = data?.data;

        if (!detalle) {
          throw new Error('No se obtuvo información del detalle.');
        }

        this.detalleVenta = detalle;
      } catch (error) {
        console.error('Error al cargar detalle de venta:', error);
        this.detalleVenta = null;
        this.detalleError = 'No se pudo cargar el detalle de la venta. Intente nuevamente.';
      } finally {
        this.detalleCargando = false;
      }
    },
    cerrarDetalle() {
      this.detalleModalAbierto = false;
      this.detalleVenta = null;
      this.detalleError = '';
      this.detalleVentaIdAbierto = 0;
    },
    formatearMonto(valor) {
      const numero = Number(valor);
      if (!Number.isFinite(numero)) return '0';
      return Number(Math.round(numero)).toLocaleString('es-PY', { minimumFractionDigits: 0 });
    },
    formatCantidadDetalle(producto) {
      const grams = Number(producto?.peso_gramos ?? producto?.cantidad ?? 0);
      if (producto?.origen_lectura === 'BALANZA' || producto?.origen_lectura === 'MANUAL_GRANEL') {
        if (grams >= 1000) return `${(grams / 1000).toFixed(3)} kg`;
        return `${grams} g`;
      }
      return producto?.cantidad ?? 0;
    },
    formatUnidadDetalle(producto) {
      if (producto?.origen_lectura === 'BALANZA' || producto?.origen_lectura === 'MANUAL_GRANEL') {
        return Number(producto?.peso_gramos ?? producto?.cantidad ?? 0) >= 1000 ? 'kg' : 'g';
      }
      return producto?.unidad_medida || '—';
    },
    formatPrecioDetalle(producto) {
      if (producto?.origen_lectura === 'BALANZA' || producto?.origen_lectura === 'MANUAL_GRANEL') {
        return `${this.formatearMonto(producto?.precio_unitario_bruto || 0)} / kg`;
      }
      return this.formatearMonto(producto?.precio_unitario_bruto || 0);
    },
    calcularSubtotalDetalle(producto) {
      const grams = Number(producto?.peso_gramos ?? producto?.cantidad ?? 0);
      if (producto?.origen_lectura === 'BALANZA' || producto?.origen_lectura === 'MANUAL_GRANEL') {
        return (Number(producto?.precio_unitario_neto ?? 0) * grams) / 1000;
      }
      return Number(producto?.precio_unitario_neto ?? 0) * Number(producto?.cantidad ?? 0);
    },
    formatearFecha(valor) {
      if (!valor) return '—';
      const fecha = new Date(valor);
      if (Number.isNaN(fecha.getTime())) return '—';
      const fechaStr = fecha.toLocaleDateString('es-PY');
      const horaStr = fecha.toLocaleTimeString('es-PY', { hour: '2-digit', minute: '2-digit' });
      return `${fechaStr} ${horaStr}`;
    },
    // Se recalcula por fila en cada render: la config de impresión puede
    // cambiar en otra pestaña sin recargar esta pantalla.
    puedeImprimir(venta) {
      return invoiceReprintService.impresionLocalHabilitada()
        && invoiceReprintService.puedeReimprimirEstado(venta?.estado)
        && Number(venta?.id_venta) > 0;
    },
    async reimprimirTicket(venta) {
      const saleId = Number(venta?.id_venta);
      if (!saleId || this.imprimiendoVentaId) return;

      this.imprimiendoVentaId = saleId;
      try {
        const resultado = await invoiceReprintService.reimprimirComprobante({
          saleId,
          invoiceNumber: venta?.nro_factura || ''
        });
        if (resultado?.omitido) return;
        const mensaje = invoiceReprintService.mensajeResultadoReimpresion(
          resultado,
          venta?.nro_factura || ''
        );
        window.alert[mensaje.tipo]?.(mensaje.texto, { title: 'Reimpresión' })
          || window.alert(mensaje.texto);
      } catch (error) {
        console.error('No se pudo reimprimir el ticket', error);
        // Se muestra el mensaje real del servicio —incluido el de timeout, que
        // avisa que el ticket pudo haber salido igual— y no una excepción
        // cruda del navegador.
        window.alert.error(
          error?.message || 'No se pudo reimprimir el ticket.',
          { title: 'Reimpresión' }
        );
      } finally {
        this.imprimiendoVentaId = null;
      }
    },
    async reimprimirDesdeDetalle() {
      await this.reimprimirTicket({
        id_venta: this.detalleVentaId,
        estado: this.detalleVenta?.cabecera?.estado,
        nro_factura: this.detalleVenta?.cabecera?.nro_factura || ''
      });
    },
    badgeClass(estado) {
      const normalized = (estado || '').toLowerCase();
      if (normalized === 'vigente') return 'bg-success';
      if (normalized === 'pendiente') return 'bg-warning text-dark';
      if (normalized === 'anulado' || normalized === 'cancelado') return 'bg-danger';
      return 'bg-secondary';
    }
  }
};
</script>

<style scoped>
.card {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}

.modal-backdrop {
  z-index: 1040;
}

.modal {
  z-index: 1050;
}
</style>
